import { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';

import { Button } from 'ui-components/components/button';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { SimpleDropdown } from '../../../../../../../../../../components/simple-dropdown';
import Global from '../../../../../../../../../../store/global-selectors';
import { messages } from './messages';
import style from './insurance-rule-builder.module.less';

export type InsuranceRule = {
  criteria: string;
  operator: string;
  value: number;
};

type InsuranceRuleBuilderProps = {
  isSaved: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onSave: (rule: InsuranceRule) => void;
  savedRule: InsuranceRule | null;
};

const InsuranceRuleSummary = ({
  criteriaLabel,
  formattedValue,
  onEdit,
  operatorLabel,
}: {
  criteriaLabel: string;
  formattedValue: string;
  onEdit: () => void;
  operatorLabel: string;
}) => (
  <FlexRow className={style['saved-rule-container']} gap={Gap.Space12}>
    <Text className={style['saved-rule-label']} textType={TextType.Label} value={messages.ruleBuilderLabel} />
    <Text className={style['saved-rule-chip']} textType={TextType.Caption} value={criteriaLabel} />
    <Text className={style['saved-rule-chip']} textType={TextType.Caption} value={operatorLabel} />
    <Text className={style['saved-rule-chip']} textType={TextType.Caption} value={formattedValue} />
    <div className={style['saved-rule-spacer']} />
    <Button className={style['edit-button']} onClick={onEdit} theme={THEME_CONSTANTS.link}>
      <FontAwesomeIcon icon={faPen} size='sm' />
      <Text textType={TextType.Caption} value={messages.editButton} />
    </Button>
  </FlexRow>
);

export const InsuranceRuleBuilder = ({ onSave, isSaving, isSaved, onEdit, savedRule }: InsuranceRuleBuilderProps) => {
  const intl = useIntl();
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || 'USD';
  const [criteria, setCriteria] = useState(savedRule?.criteria ?? 'orderTotal');
  const [operator, setOperator] = useState(savedRule?.operator ?? 'greaterThan');
  const [ruleValue, setRuleValue] = useState<string | number | null | undefined>(savedRule?.value ?? 100);

  const criteriaItems = useMemo(() => [{ key: 'orderTotal', name: intl.formatMessage(messages.criteriaOrderTotal) }], [intl]);

  const operatorItems = useMemo(
    () => [
      { key: 'greaterThan', name: intl.formatMessage(messages.operatorGreaterThan) },
      { key: 'lessThan', name: intl.formatMessage(messages.operatorLessThan) },
      { key: 'equalTo', name: intl.formatMessage(messages.operatorEqualTo) },
      { key: 'greaterThanOrEqualTo', name: intl.formatMessage(messages.operatorGreaterThanOrEqual) },
      { key: 'lessThanOrEqualTo', name: intl.formatMessage(messages.operatorLessThanOrEqual) },
    ],
    [intl],
  );

  const isValueValid = ruleValue != null && ruleValue !== '' && !Number.isNaN(Number(ruleValue)) && Number(ruleValue) > 0;

  const handleSave = useCallback(() => {
    if (!isValueValid) return;
    onSave({ criteria, operator, value: Number(ruleValue) });
  }, [criteria, operator, ruleValue, onSave, isValueValid]);

  if (isSaved) {
    const displayCriteria = savedRule?.criteria ?? criteria;
    const displayOperator = savedRule?.operator ?? operator;
    const displayValue = savedRule?.value ?? (Number(ruleValue) || 0);
    const criteriaLabel = criteriaItems.find((c) => c.key === displayCriteria)?.name ?? '';
    const operatorLabel = operatorItems.find((o) => o.key === displayOperator)?.name ?? '';
    const formattedValue = intl.formatNumber(displayValue, { style: 'currency', currency: sellerCurrencyCode });

    return (
      <InsuranceRuleSummary criteriaLabel={criteriaLabel} formattedValue={formattedValue} onEdit={onEdit} operatorLabel={operatorLabel} />
    );
  }

  return (
    <FlexColumn className={style['rule-builder']}>
      <Text className={style['rule-builder-label']} textType={TextType.Label} value={messages.ruleBuilderLabel} />
      <FlexRow className={style['rule-builder-row']} gap={Gap.Space8}>
        <div className={style['criteria-dropdown']}>
          <SimpleDropdown
            dropdownMenuShouldHaveWidthOfButton
            fullWidth
            items={criteriaItems}
            onItemSelected={setCriteria}
            selectedItemKey={criteria}
          />
        </div>
        <div className={style['operator-dropdown']}>
          <SimpleDropdown
            dropdownMenuShouldHaveWidthOfButton
            fullWidth
            items={operatorItems}
            onItemSelected={setOperator}
            selectedItemKey={operator}
          />
        </div>
        <div className={style['currency-input-wrapper']}>
          <CurrencyInput
            className={style['currency-input-height']}
            currencyCode={sellerCurrencyCode}
            disallowNegative
            onChange={setRuleValue}
            value={ruleValue ?? 0}
          />
        </div>
        <Button className={style['save-button']} disabled={isSaving || !isValueValid} onClick={handleSave} theme={THEME_CONSTANTS.link}>
          <Text textType={TextType.Caption} value={messages.saveButton} />
        </Button>
      </FlexRow>
    </FlexColumn>
  );
};
